import * as VideoConference from "video-conference";
import AgoraRTC, {
    IAgoraRTCClient,
    IAgoraRTCRemoteUser,
} from "agora-rtc-sdk-ng";
import MessageHelper from "../../../helpers/MessageHelper";
import EventApi from "../../../api/EventsApi";
import ErrorHandler from "../../../helpers/ErrorHandler";

export default class VideoManager implements VideoConference.VideoManagerInterface {
    public devicesManager: VideoConference.DevicesManagerInterface | null = null;
    private client: IAgoraRTCClient;
    public localTracks: VideoConference.LocalTracks = {
        videoTrack: null,
        audioTrack: null,
    };

    public published: boolean = false;

    constructor()
    {
        AgoraRTC.enableLogUpload();
        AgoraRTC.setLogLevel(0);

        this.client = AgoraRTC.createClient({
            codec: "vp8",
            mode: "rtc",
        });

        EventApi.triggerEvent(EventApi.VIDEO_CONFERENCE_READY);
    }

    public async init(user: string): Promise<void>
    {
        await navigator.mediaDevices.getUserMedia({audio: true, video: true}).then(() => {
            EventApi.triggerEvent(EventApi.DEVICES_ACCESS_GRANTED);
        });
    }

    public connectToConf(roomId: string, playerId: string): void
    {
        this.bindCallbacks();

        const appId = process.env.MIX_AGORA_APP_ID;

        if (typeof appId === 'undefined') {
            return;
        }

        MessageHelper.sendMessage('getConferenceUserToken', {
            'key': 'key',
            'provider': 'agora',
        });

        EventApi.addEventListener(EventApi.LOGIN_WITH_TOKEN, (event: any) => {
            this.client.join(appId, roomId, event.detail.token, playerId).then(() => {
                EventApi.triggerEvent('callConnected');
            });
        });
    }

    public async publishTracks(): Promise<void>
    {
        await this.client.unpublish();

        if (this.localTracks.videoTrack || this.localTracks.audioTrack) {

            if (this.localTracks.videoTrack && this.localTracks.videoTrack.enabled) {
                await this.client.publish(this.localTracks.videoTrack);
            }

            if (this.localTracks.audioTrack && this.localTracks.audioTrack.enabled) {
                await this.client.publish(this.localTracks.audioTrack);
            }

            this.published = true;
        }
    }

    public closeSettings(): void
    {
        if (this.localTracks.videoTrack) {

            let holder = document.getElementById('localUserVideo');
            if (holder) {
                holder.innerHTML = '';
            }

            this.localTracks.videoTrack.play("localUserVideo", {mirror: false});
        }
    }

    public audioToggle(audioStatus: boolean): void
    {
    }

    public async cameraToggle(cameraStatus: boolean): Promise<void>
    {
        await this.localTracks.videoTrack?.setEnabled(cameraStatus).then(() => {
            if (this.devicesManager) {
                this.devicesManager.setCameraStatus(cameraStatus);
            }
        });
    }

    public checkBrowser(): boolean
    {
        return true;
    }

    public async disconnect(): Promise<void>
    {

        if (this.localTracks.videoTrack) {
            this.localTracks.videoTrack.stop();
            this.localTracks.videoTrack.close();
        }

        if (this.localTracks.audioTrack) {
            this.localTracks.audioTrack.stop();
            this.localTracks.audioTrack.close();
        }

        await this.client.leave();
    }

    public getLogs(): Array<string>
    {
        return [];
    }

    public async microphoneToggle(microphoneStatus: boolean): Promise<void>
    {
        await this.localTracks.audioTrack?.setEnabled(microphoneStatus).then(() => {
            if (this.devicesManager) {
                this.devicesManager.setMicrophoneStatus(microphoneStatus);
            }
        });
    }

    public render(videoHolders: Array<HTMLElement>): void
    {
        const remoteUsers: Array<IAgoraRTCRemoteUser> = this.client.remoteUsers;
        let processed: Array<string> = [];

        remoteUsers.forEach((remoteUser: IAgoraRTCRemoteUser) => {
            const id = remoteUser.uid.toString();

            if (processed.indexOf(id) === -1) {
                processed.push(id);

                const filtered = videoHolders.filter(holder => holder.getAttribute('data-endpoint-id') === id);

                if (typeof filtered[0] !== 'undefined') {
                    const holder = filtered[0];
                    holder.innerHTML = '';

                    if (typeof remoteUser.videoTrack !== 'undefined') {
                        remoteUser.videoTrack.play(holder);
                    }

                    if (typeof remoteUser.audioTrack !== 'undefined') {
                        remoteUser.audioTrack.play();
                    }
                }
            }
        });
    }

    public setDevicesManager(manager: VideoConference.DevicesManagerInterface): void
    {
        this.devicesManager = manager;
    }

    /**
     * Привязать события
     * @private
     */
    private bindCallbacks(): void
    {
        this.client.on('user-joined', (user) => {
            this.handleUserJoined(user);
        });

        this.client.on('user-left', (user) => {
            this.handleUserLeft(user);
        });

        this.client.on('user-published', (user, mediaType) => {
            this.handleUserPublished(user, mediaType);
        });
    }

    /**
     * Подключение других пользователей
     * @param user
     * @private
     */
    private handleUserJoined(user: IAgoraRTCRemoteUser): void
    {
        let id = user.uid;
        EventApi.triggerEvent('endpointAdded');
    }

    /**
     * Отключение других пользователей
     * @param user
     * @private
     */
    private handleUserLeft(user: IAgoraRTCRemoteUser): void
    {
        const id = user.uid;
        EventApi.triggerEvent(EventApi.REMOTE_MEDIA_REMOVED);
        ErrorHandler.info(`[WebSDk] MediaRenderer was removed ID: ${id}`);
    }

    /**
     * Публикация видео-аудио потока пользователя
     * @param user
     * @param mediaType
     * @private
     */
    private async handleUserPublished(user: IAgoraRTCRemoteUser, mediaType: "audio" | "video")
    {
        await this.client.subscribe(user, mediaType).then(() => {
            EventApi.triggerEvent(EventApi.REMOTE_MEDIA_ADDED, user.uid);
            ErrorHandler.info(`[WebSDk] New MediaRenderer ID: ${user.uid}`);
        });
    }

}
